import { ADMIN_PAGES } from './adminPages';

// Single source of truth for the "Settings" hub page (pages/Setting.jsx) and
// the sidebar's flat "Settings" link visibility check (components/Sidebar/SideBar.jsx).
// Icons are referenced by string key here (this file has no JSX) and resolved
// to react-icons components locally in each consumer.

export const SETTINGS_CATEGORIES = [
  { id: 'account',   label: 'Account & Security',  iconKey: 'lock' },
  { id: 'network',   label: 'Network & Access',     iconKey: 'shield' },
  { id: 'numbers',   label: 'Numbers & Lists',      iconKey: 'db' },
  { id: 'telephony', label: 'Telephony & Channels', iconKey: 'cog' },
  { id: 'dialer',    label: 'Dialer',               iconKey: 'transfer' },
  { id: 'obd',       label: 'OBD Campaign Setup',   iconKey: 'userPlus' },
  { id: 'voicebot',  label: 'VoiceBot',             iconKey: 'robot' },
  { id: 'video',     label: 'Video & Jitsi',        iconKey: 'video' },
  { id: 'messaging', label: 'Messaging',            iconKey: 'message' },
  { id: 'audio',     label: 'Audio',                iconKey: 'audio' },
];

export const SETTINGS_ITEMS = [
  // Account & Security
  { path: '/ChangePwd', name: 'Change Password', category: 'account', iconKey: 'lock', description: 'Update your login password.' },

  // Network & Access
  { path: '/AllowIP',   name: 'Allow IP',   category: 'network', iconKey: 'shield', description: 'Whitelist IP addresses allowed to log in.' },
  { path: '/AllowPort', name: 'Allow Port', category: 'network', iconKey: 'shield', description: 'Manage allowed network ports.' },
  { path: '/Fail2ban',  name: 'Fail2ban',   category: 'network', iconKey: 'shield', description: 'Brute-force login protection rules.' },

  // Numbers & Lists
  { path: '/UploadNumber',    name: 'Upload Number',     category: 'numbers', iconKey: 'db', description: 'Bulk upload phone numbers.' },
  { path: '/BlockNumber',     name: 'Block Number',      category: 'numbers', iconKey: 'db', description: 'Block numbers from being dialed.' },
  { path: '/AssignDid',       name: 'Assign DID',        category: 'numbers', iconKey: 'db', description: 'Assign DID numbers to campaigns/agents.' },
  { path: '/TransferNumber',  name: 'Transfer Number',   category: 'numbers', iconKey: 'transfer', description: 'Configure call-transfer destination numbers.' },
  { path: '/UploadObdNumber', name: 'Upload OBD Number', category: 'numbers', iconKey: 'db', description: 'Bulk upload numbers for OBD campaigns.' },
  { path: '/ObdBlockNumber',  name: 'Block OBD Number',  category: 'numbers', iconKey: 'db', description: 'Block numbers from OBD dialing.' },

  // Telephony & Channels
  { path: '/ChannelSetting',    name: 'Channel Setting',     category: 'telephony', iconKey: 'cog', description: 'Configure trunk/channel capacity.' },
  { path: '/TrunkSettings',     name: 'Trunk Settings',      category: 'telephony', iconKey: 'cog', description: 'Manage the shared trunk catalog used by Channel Setting.' },
  { path: '/Break',             name: 'Break Setting',       category: 'telephony', iconKey: 'cog', description: 'Configure agent break types and limits.' },
  { path: '/PjsipEndpoints',    name: 'PJSIP Endpoints',     category: 'telephony', iconKey: 'cog', description: 'Manage PJSIP endpoint configuration.' },
  { path: '/ObdChannelSetting', name: 'OBD Channel Setting', category: 'telephony', iconKey: 'cog', description: 'Configure channel capacity for OBD campaigns.' },

  // Dialer
  { path: '/ObdDialerSettings', name: 'OBD Dialer Settings', category: 'dialer', iconKey: 'transfer', description: 'Pacing/retry configuration for the OBD dialer.' },
  { path: '/DialerSettings',    name: 'Dialer Settings',     category: 'dialer', iconKey: 'transfer', description: 'Global outbound dialer configuration.' },

  // OBD Campaign Setup
  { path: '/AddObdAgent',  name: 'Add Agent', category: 'obd', iconKey: 'userPlus', description: 'Create OBD agents.' },
  { path: '/ObdRecording', name: 'Recording', category: 'obd', iconKey: 'cog', description: 'Configure OBD call recording.' },

  // VoiceBot
  { path: '/VoiceBotSettings', name: 'VoiceBot Settings', category: 'voicebot', iconKey: 'robot', description: 'Configure voice profiles and AI models.' },
  { path: '/VoiceBotQuotas',   name: 'VoiceBot Quotas',   category: 'voicebot', iconKey: 'robot', description: 'View VoiceBot usage quotas.' },
  { path: '/VoiceBotAccess',   name: 'VoiceBot Access',   category: 'voicebot', iconKey: 'robot', description: 'Grant/revoke VoiceBot access per admin.' },

  // Video & Jitsi
  { path: '/JitsiSettings',       name: 'Video Access',      category: 'video', iconKey: 'video', description: 'Configure video-call access.' },
  { path: '/JitsiWidgetSettings', name: 'Customer Widget',   category: 'video', iconKey: 'video', description: 'Configure the customer-facing video widget.' },
  { path: '/JitsiDiagnostics',    name: 'Video Diagnostics', category: 'video', iconKey: 'video', description: 'Run video/Jitsi connection diagnostics.' },

  // Messaging
  { path: '/SmsportSetting', name: 'SMS Port Setting', category: 'messaging', iconKey: 'message', description: 'Configure SMS gateway ports.' },

  // Audio
  { path: '/UploadAudio', name: 'Upload Audio', category: 'audio', iconKey: 'audio', description: 'Upload and approve IVR/prompt audio files.' },
];

export const SETTINGS_ITEMS_BY_PATH = Object.fromEntries(SETTINGS_ITEMS.map((i) => [i.path, i]));

// Pure data move from SideBar.jsx's four static Settings subRoutes arrays —
// membership only, display order preserved. Names/icons now live in SETTINGS_ITEMS.
export const ADMIN_SETTINGS_PATHS = [
  '/UploadAudio', '/UploadNumber', '/BlockNumber', '/ChangePwd', '/AllowIP',
  '/Break', '/TransferNumber', '/ChannelSetting', '/JitsiSettings', '/JitsiWidgetSettings',
];

export const SUPERADMIN_SETTINGS_PATHS = [
  '/SmsportSetting', '/ChannelSetting', '/TrunkSettings', '/AssignDid', '/AllowIP', '/AllowPort',
  '/Fail2ban', '/PjsipEndpoints', '/ObdDialerSettings', '/DialerSettings',
  '/UploadAudio', '/UploadNumber', '/ChangePwd', '/VoiceBotSettings',
  '/VoiceBotQuotas', '/VoiceBotAccess', '/JitsiDiagnostics', '/JitsiSettings',
  '/JitsiWidgetSettings',
];

export const RESELLER_SETTINGS_PATHS = ['/ChannelSetting', '/AssignDid', '/ChangePwd', '/UploadAudio'];

export const OBDADMIN_BASE_SETTINGS_PATHS = [
  '/AddObdAgent', '/ObdChannelSetting', '/ObdRecording', '/UploadObdNumber', '/ObdBlockNumber',
];

/**
 * ROLE_ADMIN: default-open unless restricted. Mirrors SideBar.jsx's inline
 * ALWAYS_RESTRICTED / ALWAYS_ALLOWED / hasPerms logic exactly — keep in sync
 * if that logic ever changes.
 */
export function getAdminVisibleSettingsPaths(permissions) {
  const permSet = new Set(permissions);
  const hasPerms = permissions.length > 0;
  const ALWAYS_RESTRICTED = new Set(['/Smssuits', '/SmsportSetting', '/IvrReport']);
  const ALWAYS_ALLOWED = new Set(['/VoiceBotQuotas']);
  const allowed = (p) =>
    ALWAYS_ALLOWED.has(p) ? true
    : ALWAYS_RESTRICTED.has(p) ? permSet.has(p)
    : (!hasPerms || permSet.has(p));
  return ADMIN_SETTINGS_PATHS.filter(allowed);
}

/** ROLE_TEAMLEADER: opposite default from ADMIN — empty permissions = zero access. */
export function getTeamLeaderVisibleSettingsPaths(permissions) {
  const permSet = new Set(permissions);
  return ADMIN_SETTINGS_PATHS.filter((p) => permSet.has(p));
}

/** ROLE_SUPERADMIN: unfiltered, always full list. */
export function getSuperadminVisibleSettingsPaths() {
  return SUPERADMIN_SETTINGS_PATHS.slice();
}

/** ROLE_RESELLER: unfiltered, always full list. */
export function getResellerVisibleSettingsPaths() {
  return RESELLER_SETTINGS_PATHS.slice();
}

/**
 * ROLE_OBDADMIN: base 5 (always shown) + any ADMIN_PAGES item whose
 * group === 'Settings' AND whose path is in the superadmin-granted CSV.
 * This is the same item set that used to spawn a second sidebar "Settings"
 * submenu — now folded into one flat list for both the sidebar link and hub page.
 */
export function getObdAdminVisibleSettingsPaths(obdAdminPermissions) {
  const permSet = new Set(obdAdminPermissions);
  const grantedSettingsPaths = ADMIN_PAGES
    .filter((p) => p.group === 'Settings' && permSet.has(p.path))
    .map((p) => p.path);
  return [...OBDADMIN_BASE_SETTINGS_PATHS, ...grantedSettingsPaths];
}

/** Does this role currently have anything to show under Settings at all? Used by
 *  SideBar.jsx to decide whether to render the flat "/settings" link. */
export function hasAnyVisibleSettings(role, permissions) {
  switch (role) {
    case 'ROLE_ADMIN':      return getAdminVisibleSettingsPaths(permissions).length > 0;
    case 'ROLE_TEAMLEADER': return getTeamLeaderVisibleSettingsPaths(permissions).length > 0;
    case 'ROLE_SUPERADMIN': return true;
    case 'ROLE_RESELLER':   return true;
    case 'ROLE_OBDADMIN':   return true; // base 5 always present
    default:                return false;
  }
}

/**
 * Groups a flat list of visible paths into SETTINGS_CATEGORIES order,
 * omitting categories with zero visible items.
 */
export function groupSettingsPathsByCategory(visiblePaths) {
  const byCategory = new Map();
  visiblePaths.forEach((path) => {
    const item = SETTINGS_ITEMS_BY_PATH[path];
    if (!item) return; // defensive: unknown path, skip rather than throw
    if (!byCategory.has(item.category)) byCategory.set(item.category, []);
    byCategory.get(item.category).push(item);
  });
  return SETTINGS_CATEGORIES
    .filter((c) => byCategory.has(c.id))
    .map((c) => ({ category: c, items: byCategory.get(c.id) }));
}
